<template>
  <v-btn
    :to="to"
    :href="href"
    :target="href ? '_blank' : undefined"
    :rel="href ? 'noopener' : undefined"
    :type="type"
    :disabled="disabled"
    :variant="variant === 'ghost' || variant === 'ghost-light' ? 'outlined' : 'flat'"
    class="sec-btn"
    :class="`sec-btn--${variant}`"
    @click="emit('click', $event)"
  >
    <slot />
  </v-btn>
</template>

<script setup>
// The site's call-to-action button (.contact-btn / .why__button): #007AB2, #00AEFF on hover,
// 10px radius. `ghost` is the outlined secondary version. On dark (charcoal) panels use
// `bright` (DIGU Blue fill, dark text) and `ghost-light` (white outline).
defineProps({
  to: { type: [String, Object], default: undefined },
  href: { type: String, default: undefined },
  type: { type: String, default: 'button' },
  disabled: { type: Boolean, default: false },
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'ghost', 'bright', 'ghost-light'].includes(v),
  },
});

const emit = defineEmits(['click']);
</script>

<style scoped>
.sec-btn {
  height: 50px !important;
  padding-inline: 1.25rem !important;
  border-radius: var(--sec-radius) !important;
  text-transform: none;
  font-weight: 500;
  font-size: 1rem;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.sec-btn--primary {
  background: var(--sec-accent-strong);
  color: var(--sec-on-accent);
}

.sec-btn--primary:hover {
  background-color: var(--sec-accent);
  color: var(--sec-on-accent);
}

.sec-btn--ghost {
  background: transparent;
  color: var(--sec-accent-text);
  border: 1px solid var(--sec-accent-strong);
}

.sec-btn--ghost:hover {
  background-color: var(--sec-accent-subtle);
  border-color: var(--sec-accent);
}

.sec-btn--bright {
  background: var(--sec-accent);
  color: var(--sec-on-bright);
  font-weight: 700;
}

.sec-btn--bright:hover {
  background-color: var(--sec-accent-hover);
  color: var(--sec-on-bright);
}

.sec-btn--ghost-light {
  background: transparent;
  color: var(--sec-on-dark);
  border: 1px solid var(--sec-on-dark);
  font-weight: 600;
}

.sec-btn--ghost-light:hover {
  background-color: var(--sec-on-dark);
  color: var(--sec-charcoal);
}
</style>
